import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import adminService from "../../services/adminService";
import logger from "../../utils/logger";

// ==========================================
// Dashboard & Monitoring
// ==========================================

export const fetchDashboardStats = createAsyncThunk(
  "admin/fetchDashboardStats",
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.getDashboardStats();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch dashboard stats";
      logger.error("Error fetching dashboard stats:", error);
      return rejectWithValue(message);
    }
  },
);

export const fetchQueueStats = createAsyncThunk(
  "admin/fetchQueueStats",
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.getQueueStats();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch queue stats";
      logger.error("Error fetching queue stats:", error);
      return rejectWithValue(message);
    }
  },
);

export const fetchUrgentBookings = createAsyncThunk(
  "admin/fetchUrgentBookings",
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.getUrgentBookings();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch urgent bookings";
      logger.error("Error fetching urgent bookings:", error);
      return rejectWithValue(message);
    }
  },
);

export const fetchStuckBookings = createAsyncThunk(
  "admin/fetchStuckBookings",
  async (threshold = 2, { rejectWithValue }) => {
    try {
      const data = await adminService.getStuckBookings(threshold);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch stuck bookings";
      logger.error("Error fetching stuck bookings:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================
// Booking Management
// ==========================================

export const fetchBookings = createAsyncThunk(
  "admin/fetchBookings",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await adminService.getBookings(params);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch bookings";
      logger.error("Error fetching bookings:", error);
      return rejectWithValue(message);
    }
  },
);

export const fetchBookingById = createAsyncThunk(
  "admin/fetchBookingById",
  async (bookingId, { rejectWithValue }) => {
    try {
      const data = await adminService.getBookingById(bookingId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch booking";
      logger.error("Error fetching booking:", error);
      return rejectWithValue(message);
    }
  },
);

export const updateBooking = createAsyncThunk(
  "admin/updateBooking",
  async ({ bookingId, data }, { rejectWithValue }) => {
    try {
      const result = await adminService.updateBooking(bookingId, data);
      return result;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update booking";
      logger.error("Error updating booking:", error);
      return rejectWithValue(message);
    }
  },
);

export const assignProvider = createAsyncThunk(
  "admin/assignProvider",
  async ({ bookingId, serviceProviderId }, { rejectWithValue }) => {
    try {
      const data = await adminService.assignProvider(
        bookingId,
        serviceProviderId,
      );
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to assign provider";
      logger.error("Error assigning provider:", error);
      return rejectWithValue(message);
    }
  },
);

export const retryMatching = createAsyncThunk(
  "admin/retryMatching",
  async (bookingId, { rejectWithValue }) => {
    try {
      const data = await adminService.retryMatching(bookingId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to retry matching";
      logger.error("Error retrying matching:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================
// User Management
// ==========================================

export const fetchUsers = createAsyncThunk(
  "admin/fetchUsers",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await adminService.getUsers(params);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch users";
      logger.error("Error fetching users:", error);
      return rejectWithValue(message);
    }
  },
);

export const fetchUserById = createAsyncThunk(
  "admin/fetchUserById",
  async ({ userId, view = "all" }, { rejectWithValue }) => {
    try {
      const data = await adminService.getUserById(userId, view);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch user";
      logger.error("Error fetching user:", error);
      return rejectWithValue(message);
    }
  },
);

export const createUser = createAsyncThunk(
  "admin/createUser",
  async (userData, { rejectWithValue }) => {
    try {
      const data = await adminService.createUser(userData);
      return data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to create user";
      logger.error("Error creating user:", error);
      return rejectWithValue(message);
    }
  },
);

export const updateUser = createAsyncThunk(
  "admin/updateUser",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const result = await adminService.updateUser(userId, data);
      return result;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to update user";
      logger.error("Error updating user:", error);
      return rejectWithValue(message);
    }
  },
);

export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (userId, { rejectWithValue }) => {
    try {
      await adminService.deleteUser(userId);
      return userId;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to delete user";
      logger.error("Error deleting user:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================
// Service Provider Management
// ==========================================

export const fetchServiceProviders = createAsyncThunk(
  "admin/fetchServiceProviders",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await adminService.getServiceProviders(params);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch service providers";
      logger.error("Error fetching service providers:", error);
      return rejectWithValue(message);
    }
  },
);

export const fetchServiceProviderStats = createAsyncThunk(
  "admin/fetchServiceProviderStats",
  async (userId, { rejectWithValue }) => {
    try {
      const data = await adminService.getServiceProviderStats(userId);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch provider stats";
      logger.error("Error fetching provider stats:", error);
      return rejectWithValue(message);
    }
  },
);

export const updateProviderCategories = createAsyncThunk(
  "admin/updateProviderCategories",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      const result = await adminService.updateProviderCategories(userId, data);
      return result;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update provider categories";
      logger.error("Error updating provider categories:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================
// Category Approvals
// ==========================================

export const fetchPendingApprovals = createAsyncThunk(
  "admin/fetchPendingApprovals",
  async (_, { rejectWithValue }) => {
    try {
      const data = await adminService.getPendingCategoryApprovals();
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch pending approvals";
      logger.error("Error fetching pending approvals:", error);
      return rejectWithValue(message);
    }
  },
);

export const processCategoryApproval = createAsyncThunk(
  "admin/processCategoryApproval",
  async (
    { userId, categoryId, action, rejectionReason },
    { rejectWithValue },
  ) => {
    try {
      const data = await adminService.processCategoryApproval(
        userId,
        categoryId,
        action,
        rejectionReason,
      );
      return { userId, categoryId, action, result: data };
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to process approval";
      logger.error("Error processing approval:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================
// Category Management
// ==========================================

export const fetchCategories = createAsyncThunk(
  "admin/fetchCategories",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await adminService.getCategories(params);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch categories";
      logger.error("Error fetching categories:", error);
      return rejectWithValue(message);
    }
  },
);

export const createCategory = createAsyncThunk(
  "admin/createCategory",
  async (categoryData, { rejectWithValue }) => {
    try {
      const data = await adminService.createCategory(categoryData);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to create category";
      logger.error("Error creating category:", error);
      return rejectWithValue(message);
    }
  },
);

export const updateCategory = createAsyncThunk(
  "admin/updateCategory",
  async ({ categoryId, data }, { rejectWithValue }) => {
    try {
      const result = await adminService.updateCategory(categoryId, data);
      return result;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update category";
      logger.error("Error updating category:", error);
      return rejectWithValue(message);
    }
  },
);

export const deleteCategory = createAsyncThunk(
  "admin/deleteCategory",
  async (categoryId, { rejectWithValue }) => {
    try {
      await adminService.deleteCategory(categoryId);
      return categoryId;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete category";
      logger.error("Error deleting category:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================// Reviews Management
// ==========================================

export const fetchReviews = createAsyncThunk(
  "admin/fetchReviews",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await adminService.getReviews(params);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch reviews";
      logger.error("Error fetching reviews:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================
// Pricing Config Management
// ==========================================

export const fetchPricingConfigs = createAsyncThunk(
  "admin/fetchPricingConfigs",
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await adminService.getPricingConfigs(params);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to fetch pricing configs";
      logger.error("Error fetching pricing configs:", error);
      return rejectWithValue(message);
    }
  },
);

export const createPricingConfig = createAsyncThunk(
  "admin/createPricingConfig",
  async (configData, { rejectWithValue }) => {
    try {
      const data = await adminService.createPricingConfig(configData);
      return data;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to create pricing config";
      logger.error("Error creating pricing config:", error);
      return rejectWithValue(message);
    }
  },
);

export const updatePricingConfig = createAsyncThunk(
  "admin/updatePricingConfig",
  async ({ configId, data }, { rejectWithValue }) => {
    try {
      const result = await adminService.updatePricingConfig(configId, data);
      return result;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to update pricing config";
      logger.error("Error updating pricing config:", error);
      return rejectWithValue(message);
    }
  },
);

export const deletePricingConfig = createAsyncThunk(
  "admin/deletePricingConfig",
  async (configId, { rejectWithValue }) => {
    try {
      await adminService.deletePricingConfig(configId);
      return configId;
    } catch (error) {
      const message =
        error.response?.data?.message || "Failed to delete pricing config";
      logger.error("Error deleting pricing config:", error);
      return rejectWithValue(message);
    }
  },
);

// ==========================================// Initial State
// ==========================================

const initialState = {
  // Dashboard
  dashboardStats: null,
  dashboardLoading: false,
  dashboardError: null,

  // Queue Stats
  queueStats: null,
  queueStatsLoading: false,
  queueStatsError: null,

  // Urgent Bookings
  urgentBookings: [],
  urgentBookingsLoading: false,
  urgentBookingsError: null,

  // Stuck Bookings
  stuckBookings: [],
  stuckBookingsLoading: false,
  stuckBookingsError: null,

  // Bookings
  bookings: [],
  bookingsLoading: false,
  bookingsError: null,
  bookingsPagination: { page: 1, limit: 20, totalPages: 0, totalResults: 0 },

  // Current Booking (detail view)
  currentBooking: null,
  currentBookingLoading: false,
  currentBookingError: null,

  // Users
  users: [],
  usersLoading: false,
  usersError: null,
  usersPagination: { page: 1, limit: 20, totalPages: 0, totalResults: 0 },

  // Current User (detail view)
  currentUser: null,
  currentUserLoading: false,
  currentUserError: null,

  // Service Providers
  serviceProviders: [],
  serviceProvidersLoading: false,
  serviceProvidersError: null,
  serviceProvidersPagination: {
    page: 1,
    limit: 20,
    totalPages: 0,
    totalResults: 0,
  },

  // Pending Approvals
  pendingApprovals: [],
  pendingApprovalsLoading: false,
  pendingApprovalsError: null,

  // Categories
  categories: [],
  categoriesLoading: false,
  categoriesError: null,

  // Reviews
  reviews: [],
  reviewsLoading: false,
  reviewsError: null,
  reviewsPagination: { page: 1, limit: 20, totalPages: 0, totalResults: 0 },

  // Pricing Configs
  pricingConfigs: [],
  pricingConfigsLoading: false,
  pricingConfigsError: null,

  // Action states (for mutations)
  actionLoading: false,
  actionError: null,
  actionSuccess: null,
};

// ==========================================
// Slice
// ==========================================

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {
    clearActionState: (state) => {
      state.actionLoading = false;
      state.actionError = null;
      state.actionSuccess = null;
    },
    clearCurrentBooking: (state) => {
      state.currentBooking = null;
      state.currentBookingError = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
      state.currentUserError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Dashboard Stats
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        // Handle nested data structure - API might return { data: {...} } or direct object
        state.dashboardStats = action.payload?.data || action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })

      // Queue Stats
      .addCase(fetchQueueStats.pending, (state) => {
        state.queueStatsLoading = true;
        state.queueStatsError = null;
      })
      .addCase(fetchQueueStats.fulfilled, (state, action) => {
        state.queueStatsLoading = false;
        state.queueStats = action.payload;
      })
      .addCase(fetchQueueStats.rejected, (state, action) => {
        state.queueStatsLoading = false;
        state.queueStatsError = action.payload;
      })

      // Urgent Bookings
      .addCase(fetchUrgentBookings.pending, (state) => {
        state.urgentBookingsLoading = true;
        state.urgentBookingsError = null;
      })
      .addCase(fetchUrgentBookings.fulfilled, (state, action) => {
        state.urgentBookingsLoading = false;
        // Handle nested data structure
        const payload = action.payload?.data || action.payload;
        state.urgentBookings = Array.isArray(payload)
          ? payload
          : payload?.bookings || [];
      })
      .addCase(fetchUrgentBookings.rejected, (state, action) => {
        state.urgentBookingsLoading = false;
        state.urgentBookingsError = action.payload;
      })

      // Stuck Bookings
      .addCase(fetchStuckBookings.pending, (state) => {
        state.stuckBookingsLoading = true;
        state.stuckBookingsError = null;
      })
      .addCase(fetchStuckBookings.fulfilled, (state, action) => {
        state.stuckBookingsLoading = false;
        // Handle nested data structure
        const payload = action.payload?.data || action.payload;
        state.stuckBookings = Array.isArray(payload)
          ? payload
          : payload?.bookings || [];
      })
      .addCase(fetchStuckBookings.rejected, (state, action) => {
        state.stuckBookingsLoading = false;
        state.stuckBookingsError = action.payload;
      })

      // Bookings
      .addCase(fetchBookings.pending, (state) => {
        state.bookingsLoading = true;
        state.bookingsError = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.bookingsLoading = false;
        // Handle various response structures: { results: [] }, { data: { results: [] } }, or direct array
        const payload = action.payload?.data || action.payload;
        const results =
          payload?.results ||
          payload?.bookings ||
          (Array.isArray(payload) ? payload : []);
        state.bookings = results;
        state.bookingsPagination = {
          page: payload?.page || 1,
          limit: payload?.limit || 20,
          totalPages: payload?.totalPages || 0,
          totalResults:
            payload?.totalResults || payload?.total || results.length,
        };
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.bookingsLoading = false;
        state.bookingsError = action.payload;
      })

      // Booking Detail
      .addCase(fetchBookingById.pending, (state) => {
        state.currentBookingLoading = true;
        state.currentBookingError = null;
      })
      .addCase(fetchBookingById.fulfilled, (state, action) => {
        state.currentBookingLoading = false;
        state.currentBooking = action.payload;
      })
      .addCase(fetchBookingById.rejected, (state, action) => {
        state.currentBookingLoading = false;
        state.currentBookingError = action.payload;
      })

      // Update Booking
      .addCase(updateBooking.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateBooking.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Booking updated successfully";
        state.currentBooking = action.payload;
        // Update in list if present
        const index = state.bookings.findIndex(
          (b) => b.id === action.payload.id,
        );
        if (index !== -1) {
          state.bookings[index] = action.payload;
        }
      })
      .addCase(updateBooking.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Users
      .addCase(fetchUsers.pending, (state) => {
        state.usersLoading = true;
        state.usersError = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.usersLoading = false;
        // Handle various response structures
        const payload = action.payload?.data || action.payload;
        const results =
          payload?.results ||
          payload?.users ||
          (Array.isArray(payload) ? payload : []);
        state.users = results;
        state.usersPagination = {
          page: payload?.page || 1,
          limit: payload?.limit || 20,
          totalPages: payload?.totalPages || 0,
          totalResults:
            payload?.totalResults || payload?.total || results.length,
        };
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.usersLoading = false;
        state.usersError = action.payload;
      })

      // User Detail
      .addCase(fetchUserById.pending, (state) => {
        state.currentUserLoading = true;
        state.currentUserError = null;
      })
      .addCase(fetchUserById.fulfilled, (state, action) => {
        state.currentUserLoading = false;
        state.currentUser = action.payload;
      })
      .addCase(fetchUserById.rejected, (state, action) => {
        state.currentUserLoading = false;
        state.currentUserError = action.payload;
      })

      // Create User
      .addCase(createUser.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "User created successfully";
        state.users.unshift(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Update User
      .addCase(updateUser.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "User updated successfully";
        state.currentUser = action.payload;
        const index = state.users.findIndex((u) => u.id === action.payload.id);
        if (index !== -1) {
          state.users[index] = action.payload;
        }
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Delete User
      .addCase(deleteUser.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "User deleted successfully";
        state.users = state.users.filter((u) => u.id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Service Providers
      .addCase(fetchServiceProviders.pending, (state) => {
        state.serviceProvidersLoading = true;
        state.serviceProvidersError = null;
      })
      .addCase(fetchServiceProviders.fulfilled, (state, action) => {
        state.serviceProvidersLoading = false;
        // Handle various response structures
        const payload = action.payload?.data || action.payload;
        const results =
          payload?.results ||
          payload?.providers ||
          payload?.serviceProviders ||
          (Array.isArray(payload) ? payload : []);
        state.serviceProviders = results;
        state.serviceProvidersPagination = {
          page: payload?.page || 1,
          limit: payload?.limit || 20,
          totalPages: payload?.totalPages || 0,
          totalResults:
            payload?.totalResults || payload?.total || results.length,
        };
      })
      .addCase(fetchServiceProviders.rejected, (state, action) => {
        state.serviceProvidersLoading = false;
        state.serviceProvidersError = action.payload;
      })

      // Update Provider Categories
      .addCase(updateProviderCategories.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateProviderCategories.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Provider categories updated successfully";
        // Refresh the current user to show updated categories
        if (state.currentUser) {
          state.currentUser = action.payload;
        }
      })
      .addCase(updateProviderCategories.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Pending Approvals
      .addCase(fetchPendingApprovals.pending, (state) => {
        state.pendingApprovalsLoading = true;
        state.pendingApprovalsError = null;
      })
      .addCase(fetchPendingApprovals.fulfilled, (state, action) => {
        state.pendingApprovalsLoading = false;
        // Handle various response structures
        const payload = action.payload?.data || action.payload;
        state.pendingApprovals =
          payload?.approvals ||
          payload?.results ||
          (Array.isArray(payload) ? payload : []);
      })
      .addCase(fetchPendingApprovals.rejected, (state, action) => {
        state.pendingApprovalsLoading = false;
        state.pendingApprovalsError = action.payload;
      })

      // Process Category Approval
      .addCase(processCategoryApproval.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(processCategoryApproval.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = `Category ${action.payload.action}d successfully`;
        // Remove from pending approvals
        state.pendingApprovals = state.pendingApprovals.filter(
          (a) =>
            !(
              a.userId === action.payload.userId &&
              a.categoryId === action.payload.categoryId
            ),
        );
      })
      .addCase(processCategoryApproval.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Categories
      .addCase(fetchCategories.pending, (state) => {
        state.categoriesLoading = true;
        state.categoriesError = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categoriesLoading = false;
        // Handle various response structures: { categories: [] }, { results: [] }, { data: {...} }
        const payload = action.payload?.data || action.payload;
        state.categories =
          payload?.categories ||
          payload?.results ||
          (Array.isArray(payload) ? payload : []);
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.categoriesLoading = false;
        state.categoriesError = action.payload;
      })

      // Create Category
      .addCase(createCategory.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Category created successfully";
        state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Update Category
      .addCase(updateCategory.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Category updated successfully";
        const index = state.categories.findIndex(
          (c) => c.id === action.payload.id,
        );
        if (index !== -1) {
          state.categories[index] = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Delete Category
      .addCase(deleteCategory.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Category deleted successfully";
        state.categories = state.categories.filter(
          (c) => c.id !== action.payload,
        );
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Reviews
      .addCase(fetchReviews.pending, (state) => {
        state.reviewsLoading = true;
        state.reviewsError = null;
      })
      .addCase(fetchReviews.fulfilled, (state, action) => {
        state.reviewsLoading = false;
        const payload = action.payload?.data || action.payload;
        const results =
          payload?.results ||
          payload?.reviews ||
          (Array.isArray(payload) ? payload : []);
        state.reviews = results;
        state.reviewsPagination = {
          page: payload?.page || 1,
          limit: payload?.limit || 20,
          totalPages: payload?.totalPages || 0,
          totalResults:
            payload?.totalResults || payload?.total || results.length,
        };
      })
      .addCase(fetchReviews.rejected, (state, action) => {
        state.reviewsLoading = false;
        state.reviewsError = action.payload;
      })

      // Pricing Configs
      .addCase(fetchPricingConfigs.pending, (state) => {
        state.pricingConfigsLoading = true;
        state.pricingConfigsError = null;
      })
      .addCase(fetchPricingConfigs.fulfilled, (state, action) => {
        state.pricingConfigsLoading = false;
        const payload = action.payload?.data || action.payload;
        state.pricingConfigs = Array.isArray(payload)
          ? payload
          : payload?.results || [];
      })
      .addCase(fetchPricingConfigs.rejected, (state, action) => {
        state.pricingConfigsLoading = false;
        state.pricingConfigsError = action.payload;
      })

      // Create Pricing Config
      .addCase(createPricingConfig.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(createPricingConfig.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Pricing config created successfully";
        state.pricingConfigs.push(action.payload);
      })
      .addCase(createPricingConfig.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Update Pricing Config
      .addCase(updatePricingConfig.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(updatePricingConfig.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Pricing config updated successfully";
        const index = state.pricingConfigs.findIndex(
          (c) => c.id === action.payload.id,
        );
        if (index !== -1) {
          state.pricingConfigs[index] = action.payload;
        }
      })
      .addCase(updatePricingConfig.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      })

      // Delete Pricing Config
      .addCase(deletePricingConfig.pending, (state) => {
        state.actionLoading = true;
        state.actionError = null;
      })
      .addCase(deletePricingConfig.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.actionSuccess = "Pricing config deleted successfully";
        state.pricingConfigs = state.pricingConfigs.filter(
          (c) => c.id !== action.payload,
        );
      })
      .addCase(deletePricingConfig.rejected, (state, action) => {
        state.actionLoading = false;
        state.actionError = action.payload;
      });
  },
});

export const { clearActionState, clearCurrentBooking, clearCurrentUser } =
  adminSlice.actions;

export default adminSlice.reducer;

// ==========================================
// Selectors
// ==========================================

// Dashboard
export const selectDashboardStats = (state) => state.admin.dashboardStats;
export const selectDashboardLoading = (state) => state.admin.dashboardLoading;
export const selectDashboardError = (state) => state.admin.dashboardError;

// Queue Stats
export const selectQueueStats = (state) => state.admin.queueStats;
export const selectQueueStatsLoading = (state) => state.admin.queueStatsLoading;

// Urgent Bookings
export const selectUrgentBookings = (state) => state.admin.urgentBookings;
export const selectUrgentBookingsLoading = (state) =>
  state.admin.urgentBookingsLoading;

// Stuck Bookings
export const selectStuckBookings = (state) => state.admin.stuckBookings;
export const selectStuckBookingsLoading = (state) =>
  state.admin.stuckBookingsLoading;

// Bookings
export const selectBookings = (state) => state.admin.bookings;
export const selectBookingsLoading = (state) => state.admin.bookingsLoading;
export const selectBookingsError = (state) => state.admin.bookingsError;
export const selectBookingsPagination = (state) =>
  state.admin.bookingsPagination;

// Current Booking
export const selectCurrentBooking = (state) => state.admin.currentBooking;
export const selectCurrentBookingLoading = (state) =>
  state.admin.currentBookingLoading;
export const selectCurrentBookingError = (state) =>
  state.admin.currentBookingError;

// Users
export const selectUsers = (state) => state.admin.users;
export const selectUsersLoading = (state) => state.admin.usersLoading;
export const selectUsersError = (state) => state.admin.usersError;
export const selectUsersPagination = (state) => state.admin.usersPagination;

// Current User
export const selectCurrentUser = (state) => state.admin.currentUser;
export const selectCurrentUserLoading = (state) =>
  state.admin.currentUserLoading;
export const selectCurrentUserError = (state) => state.admin.currentUserError;

// Service Providers
export const selectServiceProviders = (state) => state.admin.serviceProviders;
export const selectServiceProvidersLoading = (state) =>
  state.admin.serviceProvidersLoading;
export const selectServiceProvidersError = (state) =>
  state.admin.serviceProvidersError;
export const selectServiceProvidersPagination = (state) =>
  state.admin.serviceProvidersPagination;

// Pending Approvals
export const selectPendingApprovals = (state) => state.admin.pendingApprovals;
export const selectPendingApprovalsLoading = (state) =>
  state.admin.pendingApprovalsLoading;
export const selectPendingApprovalsError = (state) =>
  state.admin.pendingApprovalsError;

// Categories
export const selectCategories = (state) => state.admin.categories;
export const selectCategoriesLoading = (state) => state.admin.categoriesLoading;
export const selectCategoriesError = (state) => state.admin.categoriesError;

// Reviews
export const selectReviews = (state) => state.admin.reviews;
export const selectReviewsLoading = (state) => state.admin.reviewsLoading;
export const selectReviewsError = (state) => state.admin.reviewsError;
export const selectReviewsPagination = (state) => state.admin.reviewsPagination;

// Action State
export const selectActionLoading = (state) => state.admin.actionLoading;
export const selectActionError = (state) => state.admin.actionError;
export const selectActionSuccess = (state) => state.admin.actionSuccess;

// Pricing Configs
export const selectPricingConfigs = (state) => state.admin.pricingConfigs;
export const selectPricingConfigsLoading = (state) =>
  state.admin.pricingConfigsLoading;
export const selectPricingConfigsError = (state) =>
  state.admin.pricingConfigsError;
